import { z } from 'zod';
/**
 * Kiểm thử — docs/plan-testing-tab.md.
 *
 * Thay ba sheet Excel. Khác biệt cốt lõi so với bảng tính: **vòng test là hàng,
 * không phải cột**. Sheet "Bug tổng hợp" đang lặp bốn cột cho mỗi vòng, nên vòng
 * thứ năm là thêm bốn cột và mọi công thức cũ lệch theo.
 */
export const TestCaseKind = z.enum(['ui', 'api']);
export const TestPriority = z.enum(['high', 'medium', 'low']);
/**
 * Kết quả một lượt chạy. **Không có bản ghi = chưa chạy** — không cần giá trị riêng.
 *
 * `blocked` là giá trị file Excel thiếu, và nó khác `fail` về bản chất: "không
 * test được vì môi trường hỏng" không phải một lỗi của sản phẩm. Gộp hai thứ sẽ
 * thổi phồng số lỗi và làm mọi tỉ lệ ở báo cáo sai theo.
 */
export const TestExecStatus = z.enum(['pass', 'fail', 'blocked', 'skipped']);
export const BugSeverity = z.enum(['critical', 'high', 'medium', 'low']);
/**
 * Vòng đời một lỗi.
 *
 * ⚠️ Chỉ **kết quả test của một vòng** đưa được lỗi sang `closed` hoặc
 * `reopened`. Không có đường nào từ trạng thái task sang đây: task xong mà lỗi
 * chưa đóng là trạng thái ĐÚNG (dev làm xong, tester chưa xác nhận).
 */
export const BugState = z.enum(['open', 'fixing', 'retest', 'closed', 'reopened', 'rejected']);
export const BugDevState = z.enum(['pending', 'fixed', 'wont_fix']);
export const BugTestState = z.enum(['pending', 'pass', 'fail']);
// ── Bộ test case ────────────────────────────────────────────────────────────
export const CreateTestSuiteInput = z.object({
    name: z.string().trim().min(1).max(200),
});
export const CreateTestCaseInput = z.object({
    /** Mã người đọc dùng để gọi tên nhau: `AUTH-01`. Duy nhất trong một dự án. */
    code: z.string().trim().min(1).max(40),
    title: z.string().trim().min(1).max(500),
    kind: TestCaseKind.default('ui'),
    suiteId: z.uuid().nullable().optional(),
    preconditions: z.string().max(20_000).nullable().optional(),
    /** Nhiều dòng, đánh số trong nội dung — giữ nguyên như người viết gõ. */
    steps: z.string().max(20_000).nullable().optional(),
    expected: z.string().max(20_000).nullable().optional(),
    /** Truy vết ngược về yêu cầu nghiệp vụ — cột "Quy tắc" của file. */
    requirementRef: z.string().max(60).nullable().optional(),
    priority: TestPriority.default('medium'),
    /** Chỉ có nghĩa với `kind = api`. */
    tool: z.string().max(60).nullable().optional(),
    endpoint: z.string().max(300).nullable().optional(),
});
export const UpdateTestCaseInput = CreateTestCaseInput.partial().omit({ code: true }).extend({
    /** Ngừng dùng nhưng **không** xoá: kết quả các vòng cũ vẫn trỏ vào case này. */
    isActive: z.boolean().optional(),
});
export const DuplicateTestCaseInput = z.object({
    newCode: z.string().trim().min(1).max(40),
});
export const CreateTestEnvironmentInput = z.object({
    name: z.string().trim().min(1).max(100),
});
export const UpdateTestEnvironmentInput = z.object({
    name: z.string().trim().min(1).max(100).optional(),
    isActive: z.boolean().optional(),
});
// ── Vòng test và kết quả chạy ───────────────────────────────────────────────
export const OpenTestRoundInput = z.object({
    name: z.string().max(200).nullable().optional(),
    note: z.string().max(20_000).nullable().optional(),
});
/**
 * Ghi kết quả một hoặc nhiều **ô** (case × môi trường) trong một vòng.
 *
 * `environmentIds` rỗng nghĩa là case không phụ thuộc môi trường (case API).
 * Nhiều phần tử = nút "áp cho mọi môi trường" — một lần bấm thay sáu.
 */
export const RecordTestExecutionInput = z.object({
    caseId: z.uuid(),
    environmentIds: z.array(z.uuid()).max(50).default([]),
    status: TestExecStatus,
    actualResult: z.string().max(20_000).nullable().optional(),
    note: z.string().max(20_000).nullable().optional(),
    /** Lỗi ghi ra từ lượt chạy này, nếu có. */
    bugId: z.uuid().nullable().optional(),
});
// ── Lỗi ─────────────────────────────────────────────────────────────────────
export const ReportBugInput = z.object({
    title: z.string().trim().min(1).max(500),
    category: z.string().max(200).nullable().optional(),
    description: z.string().max(20_000).nullable().optional(),
    /**
     * Các bước tái hiện — trường file Excel **không** có.
     *
     * Một lỗi không dựng lại được là một lỗi dev không sửa được, và đó là nguyên
     * nhân số một của vòng test thứ ba.
     */
    stepsToReproduce: z.string().max(20_000).nullable().optional(),
    environmentId: z.uuid().nullable().optional(),
    severity: BugSeverity.default('medium'),
    attachmentUrls: z.array(z.url().max(1000)).max(20).default([]),
    /**
     * Ảnh/video đã tải lên (presign với `purpose: 'bug_evidence'`, đã commit) —
     * APM-BUGF-a. Phải là file của chính người báo, trong dự án này, chưa gắn
     * vào đâu.
     */
    attachmentFileIds: z.array(z.uuid()).max(10).default([]),
    /**
     * Giao luôn cho người sửa lúc ghi — APM-TEST2-a, cách Qase làm. Có ⇒ lỗi vào
     * thẳng "Đang sửa"; bỏ trống ⇒ "Chờ giao". Giao cho **người khác** cần
     * `test:manage`; tự nhận thì không.
     */
    assigneeId: z.uuid().nullable().optional(),
    dueDate: z.iso.date().nullable().optional(),
    /**
     * Ô lưới vừa Không đạt — APM-TEST2-d, plan-testing-v2 §3.4. Có ⇒ trong
     * **cùng transaction** với lỗi, ô (vòng × case × môi trường) được ghi
     * `fail` và gắn lỗi này (`test_execution.bug_id`), để vòng sau biết ô đỏ vì
     * lỗi nào. Ghi ô là việc của tester: cần `test:execute`, như route ghi kết quả.
     *
     * Case nằm **trong** tham chiếu ô chứ không là một trường riêng: hàng lỗi
     * không có cột case — case của lỗi chính là case của ô nó gắn vào.
     */
    executionRef: z
        .object({
        roundId: z.uuid(),
        caseId: z.uuid(),
        /** `null` = ô của case API (không theo môi trường). */
        environmentId: z.uuid().nullable(),
        /** "Thực tế thấy gì" — cột kết quả thực tế của ô. */
        actualResult: z.string().max(20_000).nullable().optional(),
    })
        .optional(),
});
/**
 * Sửa nội dung một lỗi — tiêu đề, mức độ, môi trường, các bước.
 *
 * **Không** có người sửa và hạn: từ APM-TEST2-c hai trường ấy chỉ đi qua
 * `AssignBugInput`, nơi duy nhất có luật "giao người khác cần PM".
 */
export const UpdateBugInput = ReportBugInput.partial().omit({
    attachmentUrls: true,
    attachmentFileIds: true,
    assigneeId: true,
    dueDate: true,
    executionRef: true,
});
// ── Luồng lỗi v2 — APM-TEST2-a, docs/plan-testing-v2.md §2 ────────────────────
/**
 * Giao lỗi cho một người (hoặc bỏ giao: `null`).
 *
 * Giao cho **người khác** cần `test:manage`; tự nhận lỗi đang "Chờ giao" thì chỉ
 * cần là thành viên đọc được tab — cách dev bấm "Tôi nhận" trên thẻ.
 */
export const AssignBugInput = z.object({
    assigneeId: z.uuid().nullable(),
    dueDate: z.iso.date().nullable().optional(),
});
/** "Đã sửa xong" — chỉ người được giao hoặc người có `test:manage`. Không cần vòng mở. */
export const MarkBugFixedInput = z.object({
    comment: z.string().trim().max(20_000).nullable().optional(),
});
/**
 * "✓ Hết lỗi" / "✗ Vẫn lỗi" — người có `test:execute` **hoặc** người đã ghi lỗi,
 * nhưng không bao giờ là người được giao sửa (plan-testing-v2 §6 câu 3).
 *
 * "Vẫn lỗi" bắt buộc một câu "vẫn thấy gì": không có nó dev nhận lại lỗi mà
 * không biết lần này khác lần trước ở đâu.
 */
export const VerifyBugInput = z
    .object({
    result: z.enum(['pass', 'fail']),
    comment: z.string().trim().max(20_000).nullable().optional(),
})
    .superRefine((value, ctx) => {
    if (value.result === 'fail' && (value.comment ?? '').length === 0) {
        ctx.addIssue({
            code: 'custom',
            path: ['comment'],
            message: 'Ghi lại vẫn thấy lỗi gì để người sửa biết lần này khác lần trước ở đâu',
        });
    }
});
/** "Không phải lỗi" (PM) hoặc "Mở lại" một lỗi đã đóng (tester) — lý do bắt buộc. */
export const BugReasonInput = z.object({
    reason: z.string().trim().min(1).max(500),
});
/**
 * Ai đang phải làm gì với lỗi này — tính ở server, một chỗ.
 *
 * - `assign`: chờ giao (`userId` = null — ai có `test:manage` giao).
 * - `fix`: người được giao đang sửa.
 * - `verify`: chờ người báo lỗi kiểm lại.
 * - `none`: đã đóng / không phải lỗi.
 */
export const BugWaitingOn = z.object({
    kind: z.enum(['assign', 'fix', 'verify', 'none']),
    userId: z.uuid().nullable(),
});
// ── View ────────────────────────────────────────────────────────────────────
export const TestSuiteView = z.object({
    id: z.uuid(),
    name: z.string(),
    position: z.number().int().nonnegative(),
});
export const TestCaseView = z.object({
    id: z.uuid(),
    suiteId: z.uuid().nullable(),
    code: z.string(),
    kind: TestCaseKind,
    title: z.string(),
    preconditions: z.string().nullable(),
    steps: z.string().nullable(),
    expected: z.string().nullable(),
    requirementRef: z.string().nullable(),
    priority: TestPriority,
    tool: z.string().nullable(),
    endpoint: z.string().nullable(),
    isActive: z.boolean(),
});
export const TestEnvironmentView = z.object({
    id: z.uuid(),
    name: z.string(),
    position: z.number().int().nonnegative(),
    isActive: z.boolean(),
});
export const TestRoundView = z.object({
    id: z.uuid(),
    number: z.number().int().positive(),
    name: z.string().nullable(),
    note: z.string().nullable(),
    startedAt: z.iso.datetime(),
    /** `null` = vòng đang mở. Đúng một vòng mở tại một thời điểm. */
    closedAt: z.iso.datetime().nullable(),
});
/** Một **ô** kết quả: vòng × case × môi trường. */
export const TestExecutionView = z.object({
    id: z.uuid(),
    caseId: z.uuid(),
    /** `null` = case không phụ thuộc môi trường (case API). */
    environmentId: z.uuid().nullable(),
    status: TestExecStatus,
    actualResult: z.string().nullable(),
    note: z.string().nullable(),
    testerId: z.uuid().nullable(),
    testedAt: z.iso.datetime().nullable(),
    bugId: z.uuid().nullable(),
});
export const BugAttachmentView = z.object({
    id: z.uuid(),
    /** Link ngoài — `null` khi đính kèm là file tải lên (APM-BUGF-a). */
    url: z.string().nullable(),
    caption: z.string().nullable(),
    /** File tải lên, tải về qua `GET /files/:id/url`. `null` với link ngoài. */
    file: z
        .object({
        fileId: z.uuid(),
        filename: z.string(),
        mimeType: z.string(),
        sizeBytes: z.number().int().nonnegative(),
        /** `deleted` = đã xoá ở trang Files; lỗi vẫn nhớ từng có bằng chứng này. */
        status: z.enum(['committed', 'deleted']),
    })
        .nullable(),
});
/** Một lượt sửa — APM-TEST2-a. Vòng là tuỳ chọn: dev sửa không cần đợi vòng mở. */
export const BugRoundResultView = z.object({
    seq: z.number().int().positive(),
    roundId: z.uuid().nullable(),
    roundNumber: z.number().int().positive().nullable(),
    devState: BugDevState,
    devComment: z.string().nullable(),
    devAt: z.iso.datetime().nullable(),
    devBy: z.uuid().nullable(),
    testState: BugTestState,
    testerComment: z.string().nullable(),
    testerId: z.uuid().nullable(),
    testedAt: z.iso.datetime().nullable(),
});
export const BugView = z.object({
    id: z.uuid(),
    /** STT trong phạm vi dự án — cách cả đội gọi tên nhau ("lỗi 12 sửa chưa"). */
    code: z.number().int().positive(),
    title: z.string(),
    category: z.string().nullable(),
    description: z.string().nullable(),
    stepsToReproduce: z.string().nullable(),
    environmentId: z.uuid().nullable(),
    severity: BugSeverity,
    state: BugState,
    reporterId: z.uuid(),
    reportedAt: z.iso.datetime(),
    assigneeId: z.uuid().nullable(),
    dueDate: z.string().nullable(),
    foundInRoundId: z.uuid().nullable(),
    attachments: z.array(BugAttachmentView),
    /** Ai đang phải làm gì — APM-TEST2-a. */
    waitingOn: BugWaitingOn,
    /** Số lần tester trả lại ("Vẫn lỗi"). Thẻ hiện "Bị trả lại · lần N". */
    reopenCount: z.number().int().nonnegative(),
});
/**
 * Việc sửa lỗi đang gắn với một lỗi — docs/plan-testing-tab.md §9.2.
 *
 * ## Vì sao chỉ có mấy trường này
 *
 * Đủ để **nhận ra** và **bấm sang**, không hơn. Task là chủ của "ai làm, bao
 * lâu, khi nào" (§9.1), nên nhân bản những trường ấy vào đây là tạo chỗ thứ hai
 * để chúng lệch nhau.
 */
export const LinkedTaskView = z.object({
    id: z.uuid(),
    /** `SHOP-42` — cách người ta gọi tên một việc. */
    taskKey: z.string(),
    title: z.string(),
    /** Tên cột hiện tại. Đọc để biết dev đã làm xong phần việc của họ chưa. */
    statusName: z.string().nullable(),
    assigneeId: z.uuid().nullable(),
});
/**
 * Một dòng của "Lịch sử" trong ngăn chi tiết lỗi — APM-TEST2-c, plan-testing-v2 §3.3.
 *
 * Gộp ba nguồn thành **một** dòng thời gian người đọc được: hàng lỗi (ghi lỗi),
 * các lượt sửa (đã sửa xong / hết lỗi / vẫn lỗi) và `event_log` (giao, bác, mở
 * lại — những thứ hàng lỗi chỉ giữ giá trị **hiện tại**). Ghép ở server, một
 * chỗ: client tự ghép thì mỗi màn hình đọc lịch sử một kiểu.
 *
 * - `reported`: `actorId` ghi lỗi; `assigneeId` = giao luôn lúc ghi (nếu có).
 * - `assigned`: `assigneeId` = người mới, `null` = bỏ giao / trả lại.
 * - `fixed` / `wont_fix`: một lượt sửa; `roundNumber` = vòng đang mở lúc báo, nếu có.
 * - `verified_pass` / `verified_fail`: kết quả kiểm lại của lượt ấy.
 * - `rejected` / `reopened`: kèm lý do ở `comment`.
 */
export const BugHistoryEntry = z.object({
    at: z.iso.datetime(),
    kind: z.enum([
        'reported',
        'assigned',
        'fixed',
        'wont_fix',
        'verified_pass',
        'verified_fail',
        'rejected',
        'reopened',
    ]),
    actorId: z.uuid().nullable(),
    assigneeId: z.uuid().nullable(),
    roundNumber: z.number().int().positive().nullable(),
    comment: z.string().nullable(),
});
/** Ô lưới đang gắn lỗi này (`test_execution.bug_id`) — dòng "Từ case" của ngăn chi tiết. */
export const BugSourceView = z.object({
    caseId: z.uuid(),
    caseCode: z.string(),
    caseTitle: z.string(),
    roundId: z.uuid(),
    roundNumber: z.number().int().positive(),
    environmentId: z.uuid().nullable(),
});
export const BugDetailView = BugView.extend({
    /** Các lượt sửa — thay cho các cột `… 1`, `… 2` của bảng tính. */
    roundResults: z.array(BugRoundResultView),
    /**
     * Việc sửa lỗi đang gắn — §9.2. Thường rỗng: phần lớn lỗi được sửa ngay trong
     * ngày và không cần một việc có kế hoạch.
     */
    tasks: z.array(LinkedTaskView),
    /** Dòng thời gian gộp, cũ trước — APM-TEST2-c. */
    history: z.array(BugHistoryEntry),
    /** Các ô kết quả gắn lỗi này, vòng mới trước. Thường 0–1 ô. */
    sources: z.array(BugSourceView),
});
/**
 * Toàn bộ dữ liệu tab Kiểm thử trong một lời gọi.
 *
 * Một endpoint chứ không năm: mở tab là dựng cả bốn khu vực cùng lúc (vòng, bộ
 * case, lưới kết quả, danh sách lỗi), và năm lời gọi song song chỉ để vẽ một
 * màn hình là năm transaction cho một lần bấm.
 */
export const TestingOverview = z.object({
    projectId: z.uuid(),
    suites: z.array(TestSuiteView),
    cases: z.array(TestCaseView),
    environments: z.array(TestEnvironmentView),
    rounds: z.array(TestRoundView),
    /** Vòng đang xem — mặc định là vòng đang mở, hoặc vòng mới nhất. */
    currentRoundId: z.uuid().nullable(),
    /** Kết quả của **vòng đang xem**, không phải mọi vòng. */
    executions: z.array(TestExecutionView),
    bugs: z.array(BugView),
});
// ── Lỗi của tôi — "Việc của tôi" ────────────────────────────────────────────
/**
 * Một lỗi đang chờ chính người xem — APM-TEST2-e, plan-testing-v2 §3.5.
 *
 * Đủ để **nhận ra** (`PAY #17`, tiêu đề, mức độ, hạn) và **bấm sang** tab Kiểm
 * thử của đúng dự án, không hơn: mô tả, bằng chứng, lượt sửa nằm ở ngăn chi
 * tiết của dự án ấy. Dòng này sống ở trang gộp nhiều dự án, nên mang theo
 * `projectKey`/`projectName` — thứ `BugView` không cần vì nó luôn ở trong một
 * dự án.
 */
export const MyBugView = z.object({
    id: z.uuid(),
    code: z.number().int().positive(),
    title: z.string(),
    severity: BugSeverity,
    state: BugState,
    /** Ngày thuần `YYYY-MM-DD` (cột `DATE`), không phải mốc giờ. */
    dueDate: z.string().nullable(),
    /** Số lần bị trả lại ("Vẫn lỗi") — nhóm "bị trả lại" hiện "lần N". */
    reopenCount: z.number().int().nonnegative(),
    projectId: z.uuid(),
    projectKey: z.string(),
    projectName: z.string(),
});
/**
 * Lỗi đang chờ tôi, chia theo **việc tôi phải làm** — không theo `bug.state`.
 *
 * - `fix` — tôi được giao, đang sửa (`open` đã có người, `fixing`).
 * - `reopened` — tôi được giao và tester vừa trả lại. Tách khỏi `fix` vì đó là
 *   việc gấp hơn: lần sửa trước chưa đủ.
 * - `verify` — tôi ghi lỗi và dev báo đã sửa xong, chờ tôi kiểm lại. Không bao
 *   giờ chứa lỗi mà chính tôi được giao (plan-testing-v2 §6 câu 3).
 *
 * Object bọc, không mảng trần: "không có lỗi nào" là ba mảng rỗng.
 */
export const MyBugsView = z.object({
    fix: z.array(MyBugView),
    reopened: z.array(MyBugView),
    verify: z.array(MyBugView),
});
// ── Báo cáo ─────────────────────────────────────────────────────────────────
/** Số ô theo trạng thái. `notRun` = ô chưa có bản ghi nào. */
export const TestCellTally = z.object({
    total: z.number().int().nonnegative(),
    pass: z.number().int().nonnegative(),
    fail: z.number().int().nonnegative(),
    blocked: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    notRun: z.number().int().nonnegative(),
});
export const TestTallyByName = TestCellTally.extend({
    id: z.uuid().nullable(),
    name: z.string(),
});
/**
 * Chỉ số của **một vòng** — docs/plan-testing-tab.md §4.
 *
 * ## Hai con số quan trọng nhất
 *
 * `fixYieldPct` = xác nhận ÷ dev báo đã sửa. Dev khai sửa 10 mà chỉ 6 pass thì
 * 60%. `reopenRatePct` là mặt còn lại: phần công sửa bị trả lại.
 *
 * Cả hai là `null` khi **dev chưa báo sửa cái nào** trong vòng ấy. `null` khác
 * `0`: "chưa có gì để tính" và "tính ra 0%" là hai điều khác hẳn nhau với người
 * đang quyết có chốt ngày phát hành hay không.
 */
export const TestingRoundMetrics = z.object({
    roundId: z.uuid(),
    roundNumber: z.number().int().positive(),
    startedAt: z.iso.datetime(),
    closedAt: z.iso.datetime().nullable(),
    /** §4.1 — tiến độ chạy test, đếm theo **ô** (case × môi trường). */
    cells: TestCellTally,
    bySuite: z.array(TestTallyByName),
    byEnvironment: z.array(TestTallyByName),
    /** Case đã chạy ít nhất một môi trường, trên tổng số case đang dùng. */
    coveredCases: z.number().int().nonnegative(),
    caseCount: z.number().int().nonnegative(),
    /** §4.2 — hiệu quả sửa lỗi. */
    bugsOpenAtStart: z.number().int().nonnegative(),
    /** Lỗi **phát sinh mới** trong vòng này. Còn nhiều nghĩa là chưa hội tụ. */
    bugsNew: z.number().int().nonnegative(),
    devReportedFixed: z.number().int().nonnegative(),
    testConfirmed: z.number().int().nonnegative(),
    testFailed: z.number().int().nonnegative(),
    bugsOpenAtEnd: z.number().int().nonnegative(),
    fixYieldPct: z.number().min(0).max(100).nullable(),
    reopenRatePct: z.number().min(0).max(100).nullable(),
});
export const TestingBreakdownRow = z.object({
    key: z.string(),
    open: z.number().int().nonnegative(),
    closed: z.number().int().nonnegative(),
});
/**
 * Báo cáo kiểm thử — docs/plan-testing-tab.md §4.
 *
 * ## Trục thời gian ở đây là **vòng**, không phải ngày
 *
 * "Cuối vòng 2 tình hình thế nào" là câu hỏi thật của người đọc, và nó không
 * ánh xạ sang một ngày cụ thể. `asOf` (luật #7) vì thế **cắt** danh sách vòng:
 * chỉ tính những vòng đã bắt đầu trước mốc ấy. Một biểu đồ đang vẽ quá khứ mà
 * không nói ra là một biểu đồ nói dối về hiện tại.
 *
 * ## ⚠️ Một chỗ xấp xỉ, nói ra thay vì giả vờ chính xác
 *
 * Lỗi bị kết luận "không phải lỗi" (`rejected`) bị loại khỏi **mọi** vòng, kể cả
 * những vòng diễn ra trước lúc ai đó kết luận thế. Thời điểm kết luận không được
 * ghi theo vòng (nó là một quyết định thủ công, không phải kết quả một vòng), nên
 * dựng lại đúng lịch sử của nó cần replay trên `event_log`. Sai lệch chỉ xuất
 * hiện với lỗi bị loại muộn, và nó luôn theo hướng **lạc quan hơn** ở các vòng cũ.
 */
export const TestingReport = z.object({
    projectId: z.uuid(),
    /** `null` = hiện tại. */
    asOf: z.string().nullable(),
    /** Thứ tự vòng **tăng dần** — đây là trục x của đường hội tụ. */
    rounds: z.array(TestingRoundMetrics),
    /** §4.4 — vài con số nữa, rẻ vì dữ liệu đã có. */
    overdueBugs: z.number().int().nonnegative(),
    /** Giờ trung bình từ lúc lỗi tới tay dev đến lúc dev báo xong. `null` = chưa đủ mẫu. */
    avgFixHours: z.number().nonnegative().nullable(),
    byCategory: z.array(TestingBreakdownRow),
    bySeverity: z.array(TestingBreakdownRow),
});
// ── Lỗi ↔ việc sửa lỗi (§9.2) ───────────────────────────────────────────────
/**
 * Tạo một việc sửa lỗi từ một lỗi — §9.2.
 *
 * ## ⚠️ `taskTypeId` là BẮT BUỘC, và đó là chủ đích
 *
 * Kế hoạch ban đầu định ngầm tra loại việc tên "Sửa lỗi". Nhưng loại ấy chỉ có
 * trong **dữ liệu seed demo**, không phải danh mục đảm bảo của mọi tổ chức — một
 * tổ chức thật hoàn toàn có thể không có nó. Ngầm tra rồi rơi về "loại đầu danh
 * sách" sẽ âm thầm gắn việc sửa lỗi vào loại "Tính năng", và mọi báo cáo theo
 * loại việc sai từ đó.
 *
 * Nên server đòi tường minh; giao diện chọn sẵn loại "Sửa lỗi" nếu dự án có.
 */
export const CreateFixTaskInput = z.object({
    taskTypeId: z.uuid(),
    /** Bỏ trống ⇒ lấy tiêu đề của lỗi. */
    title: z.string().trim().min(1).max(500).optional(),
    assigneeId: z.uuid().nullable().optional(),
    dueDate: z.iso.date().nullable().optional(),
});
export const LinkBugTaskInput = z.object({ taskId: z.uuid() });
// ── Sinh case từ việc đã làm (AI) ───────────────────────────────────────────
export const GENERATE_TEST_CASES_SKILL = 'generate_test_cases';
/**
 * Một case do AI đề xuất — **chưa** phải case thật.
 *
 * ## Vì sao `sourceTaskKey` chứ không phải `sourceTaskId`
 *
 * Hai lý do trùng nhau. Mã việc (`CRM-12`) là thứ người duyệt đọc được, nên
 * danh sách chờ duyệt gom nhóm được theo việc mà không phải tra thêm bảng. Và
 * nó **không phải một Id**: grounding check của harness đòi mọi chuỗi trông như
 * Id trong kết quả phải có mặt trong dữ liệu tool đã trả (`*taskId` là một
 * trong những tên trường bị soi), mà `get_task` cố tình không trả id của chính
 * task nó vừa đọc. Khai một `sourceTaskId` ở đây là tự dựng một cửa mà chính
 * dữ liệu tool không mở được.
 *
 * Lúc duyệt, mã này thành `requirementRef` của case — cột truy vết ngược sẵn có
 * trong bảng. Không thêm khoá ngoại case→task: một case sống lâu hơn cái việc
 * đẻ ra nó, và xoá việc không được phép kéo theo case đang chạy ở vòng nào đó.
 */
export const GeneratedTestCase = z.object({
    sourceTaskKey: z.string().min(1).max(60),
    title: z.string().min(1).max(500),
    kind: TestCaseKind.default('ui'),
    preconditions: z.string().max(4_000).nullable().default(null),
    steps: z.string().min(1).max(8_000),
    expected: z.string().min(1).max(4_000),
    priority: TestPriority.default('medium'),
    /** Căn cứ: câu nào trong mô tả việc hay tài liệu nào dẫn tới case này. */
    why: z.string().min(1).max(500),
});
export const GeneratedTestCases = z.object({
    items: z.array(GeneratedTestCase).min(1).max(40),
    /**
     * Chỗ dữ liệu không đủ để viết case cho tử tế — nói thẳng thay vì bịa.
     *
     * Đây là phần đáng đọc nhất với việc **chưa xong**: nó nói ra rằng bộ case
     * vừa sinh mỏng ở đâu, và mỏng vì thiếu gì (chưa có tài liệu, mô tả chỉ một
     * dòng). Không có mục này thì một bộ case nông trông y hệt một bộ case đủ.
     */
    gaps: z.array(z.string().max(300)).max(10).default([]),
});
/** Duyệt **một phần** gợi ý: chỉ những mục người duyệt còn tích. */
export const AcceptGeneratedCasesInput = z.object({
    indexes: z.array(z.number().int().nonnegative()).min(1).max(40),
    /** Bỏ trống = case không thuộc nhóm nào. */
    suiteId: z.uuid().nullable().optional(),
});
// ── Nhập case từ file (APM-TIMP) ────────────────────────────────────────────
/**
 * Mười cột của file mẫu, theo đúng thứ tự trong file.
 *
 * Đây là **định dạng tệp**, dùng chung ba nơi: file mẫu tải về, bộ nhận diện
 * "file này theo mẫu chưa", và ánh xạ cột mà skill `map_test_case_columns` nộp.
 * Một cột thêm ở đây mà quên ở một trong ba nơi thì file mẫu vừa tải về sẽ bị
 * chính màn hình nhập coi là "không theo mẫu".
 */
export const TEST_CASE_IMPORT_FIELDS = [
    'code',
    'suite',
    'title',
    'kind',
    'priority',
    'preconditions',
    'steps',
    'expected',
    'requirementRef',
    'endpoint',
];
export const TestCaseImportField = z.enum(TEST_CASE_IMPORT_FIELDS);
/** Trần một lượt nhập — một command, một transaction (15 giây), một event mỗi case. */
export const MAX_IMPORT_CASES = 500;
/**
 * Một dòng đã chuẩn hoá về đúng mẫu — thứ client gửi lên sau bước xem trước.
 *
 * Chuẩn hoá (đọc file, ánh xạ cột, đổi "Cao" ⇒ `high`) chạy ở **trình duyệt**:
 * tệp không bao giờ đi qua Node (luật #3), và server chỉ nhận những dòng người
 * dùng đã nhìn thấy trong bảng xem trước.
 */
export const ImportedTestCase = z.object({
    /** Bỏ trống ⇒ server cấp mã theo mã dự án (`IOT-TC01`). */
    code: z.string().trim().max(40).nullable(),
    /** Tên nhóm; chưa có nhóm cùng tên (không phân biệt hoa thường) thì tạo mới. */
    suiteName: z.string().trim().max(200).nullable(),
    title: z.string().trim().min(1).max(500),
    kind: TestCaseKind,
    priority: TestPriority,
    preconditions: z.string().max(20_000).nullable(),
    steps: z.string().max(20_000).nullable(),
    expected: z.string().max(20_000).nullable(),
    requirementRef: z.string().max(60).nullable(),
    endpoint: z.string().max(300).nullable(),
});
export const ImportTestCasesInput = z.object({
    rows: z.array(ImportedTestCase).min(1).max(MAX_IMPORT_CASES),
    /**
     * Gợi ý ánh xạ cột của AI đã dùng để dựng `rows`, nếu có.
     *
     * Bấm Nhập là **lời duyệt** gợi ý ấy (luật #6), nên gợi ý đóng lại trong cùng
     * transaction với các case — cùng lý lẽ với `AcceptGeneratedCasesCommand`.
     */
    mappingSuggestionId: z.uuid().nullable().optional(),
});
export const MAP_TEST_CASE_COLUMNS_SKILL = 'map_test_case_columns';
/** Mẫu gửi cho skill: vài chục dòng đầu của một sheet, mỗi ô đã cắt ngắn. */
export const MAX_MAPPING_SAMPLE_ROWS = 30;
export const MAX_MAPPING_SAMPLE_COLUMNS = 30;
export const MAX_MAPPING_CELL_CHARS = 160;
/** Cột nguồn (chỉ số từ 0) cho một trường của mẫu; `null` = file không có. */
const SourceColumn = z
    .number()
    .int()
    .min(0)
    .max(MAX_MAPPING_SAMPLE_COLUMNS - 1)
    .nullable();
/**
 * Ánh xạ cột do AI đề xuất — **không** phải dữ liệu case.
 *
 * ## Vì sao AI chỉ dò cột, không viết lại dòng (người dùng chọn, 2026-09-30)
 *
 * Model đọc 30 dòng đầu và nói "cột C là tên case, dòng 4 là tiêu đề"; máy áp
 * ánh xạ ấy cho cả file. Rẻ và không phụ thuộc độ dài file, và model không chạm
 * vào chữ nên không có chỗ nào để nó bịa hay "sửa giúp" một bước test. Cái giá
 * là file trộn bước với kết quả trong cùng một ô thì không tách được — ở đó
 * người dùng vẫn nhập được, chỉ là cột kết quả để trống.
 */
export const TestCaseColumnMapping = z.object({
    /** Dòng tiêu đề cột, chỉ số từ 0 trong mẫu. `null` = file không có dòng tiêu đề. */
    headerRow: z
        .number()
        .int()
        .min(0)
        .max(MAX_MAPPING_SAMPLE_ROWS - 1)
        .nullable(),
    columns: z.object({
        code: SourceColumn,
        suite: SourceColumn,
        title: SourceColumn,
        kind: SourceColumn,
        priority: SourceColumn,
        preconditions: SourceColumn,
        steps: SourceColumn,
        expected: SourceColumn,
        requirementRef: SourceColumn,
        endpoint: SourceColumn,
    }),
    /**
     * Dòng chỉ có chữ ở một ô (các ô khác trống) là **tên nhóm** cho những dòng
     * bên dưới — kiểu trình bày phổ biến của file test case viết tay.
     */
    groupRows: z.boolean(),
    /** Chữ trong file ứng với từng mức ưu tiên, chép nguyên văn ("P1", "Critical"). */
    priorityValues: z.object({
        high: z.array(z.string().max(60)).max(10),
        medium: z.array(z.string().max(60)).max(10),
        low: z.array(z.string().max(60)).max(10),
    }),
    /** Chữ trong file nghĩa là case API (các giá trị khác ⇒ giao diện). */
    apiValues: z.array(z.string().max(60)).max(10),
    /** Tối đa 5 câu: đã ánh xạ gì, cột nào bỏ qua, chỗ nào không chắc. */
    notes: z.array(z.string().max(300)).max(5),
});
//# sourceMappingURL=testing.js.map